import React, { useCallback, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import FormField from "../../dynamicFormBuilder/FormField";
import { GetLookupStaticData } from "../../om/services/SalesOrderService";
import { PrimaryLayout } from "./Layout";
import {
    TRANSACTION_TYPES, STAGES, STATUSES, toSelectOptions,
    STATUS_COMMITTED, STAGE_COMPLETED, TRANSACTION_TYPE_ADJUSTMENT, STAGE_EDITABLE_TRANSACTION_TYPES,
} from "../itemManagement/transactionsTab/codeLists";
import { EMPTY_GUID } from "../../const/constants";

function PrimaryTab(props) {
    const currentUser = props.oidc?.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const accessToken = props.oidc?.user?.access_token;

    const finalLayout = useMemo(() => PrimaryLayout, []);

    const handleSelectionChange = useCallback((name, value, e) => {
        props.handleFieldChange(name, value, e);
        // Delphi edtTransactionTypeChange: an Adjustment (type 'A') header is forced to Stage 'Completed'.
        if (name === "TRANSACTIONTYPE" && value === TRANSACTION_TYPE_ADJUSTMENT) {
            props.handleFieldChange("STAGE", STAGE_COMPLETED, e);
        }
    }, [props.handleFieldChange]);

    // Delphi ControlHeaderFields (uIMD0019.PAS:1052) + edtTransactionTypeChange (:1655): field enable/disable
    // rules, run on load (edit) and whenever the bound values change. Applied imperatively on the field refs,
    // the same way the coded dropdowns are populated.
    const applyHeaderFieldRules = useCallback(() => {
        const refs = props.formRefs?.current;
        if (!refs) return;
        const ff = props.formFields || {};
        const setDisabled = (name, disabled) => {
            const r = refs[name];
            if (r?.instance) r.instance().option("disabled", disabled);
        };
        const isNew = !ff.ROWID || ff.ROWID === EMPTY_GUID;
        const isCommitted = ff.STATUS === STATUS_COMMITTED;

        // Profile is editable only on a new transaction.
        setDisabled("RPROFILE", !isNew);
        // Transaction Type is locked once the header has detail lines.
        setDisabled("TRANSACTIONTYPE", Number(ff.TOTALLINES) > 0);
        // Transaction Date / Status / Description lock once the transaction is committed (STATUS 'Z').
        const lockCommitted = !isNew && isCommitted;
        setDisabled("TRANSACTIONDATE", lockCommitted);
        setDisabled("STATUS", lockCommitted);
        setDisabled("DESCRIPTION", lockCommitted);
        // Stage is editable only for Usage/Usage Return/Receipt/Receipt Return types.
        setDisabled("STAGE", !STAGE_EDITABLE_TRANSACTION_TYPES.includes(ff.TRANSACTIONTYPE));
    }, [props.formRefs, props.formFields]);

    const loadAllLookups = useCallback(() => {
        // Static coded dropdowns via the shared helper (re-applies the bound value once the options exist).
        const ff = props.formFields || {};
        GetLookupStaticData("TRANSACTIONTYPE", toSelectOptions(TRANSACTION_TYPES), props.formRefs, null, false, ff.TRANSACTIONTYPE);
        GetLookupStaticData("STAGE", toSelectOptions(STAGES), props.formRefs, null, false, ff.STAGE);
        GetLookupStaticData("STATUS", toSelectOptions(STATUSES), props.formRefs, null, false, ff.STATUS);
    }, [accessToken, props.formFields, props.formRefs]);

    const handlePrimaryFormLoad = useCallback(() => {
        setTimeout(() => { loadAllLookups(); applyHeaderFieldRules(); }, 100);
    }, [loadAllLookups, applyHeaderFieldRules]);

    // Re-apply when the bound values change (e.g. after loading a record for edit).
    useEffect(() => {
        loadAllLookups();
        applyHeaderFieldRules();
    }, [loadAllLookups, applyHeaderFieldRules, props.formFields?.RPROFILE, props.formFields?.TRANSACTIONTYPE, props.formFields?.STAGE, props.formFields?.STATUS,
        props.formFields?.ROWID, props.formFields?.TOTALLINES]);

    return (
        <div>
            <FormField
                currrentUser={currentUser}
                layoutId="0019d019-2a45-4c67-b1d8-0019bbccdd01"
                layoutName="IMTransactionEntryPrimaryForm"
                defaultFormSchema={finalLayout}
                formFields={props.formFields}
                setformFields={props.setformFields}
                handleFieldValueChange={handleSelectionChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={props.formRefs}
                onFormLoad={handlePrimaryFormLoad}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(PrimaryTab);
